import React, { useEffect, useState, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Col, Row, Card, Form, Button } from '@themesberg/react-bootstrap';
import axios from "axios";
import { API_URL, END_POINT_CUSTOMERS, END_POINT_INDUSTRIES, END_POINT_SUB_INDUSTRIES, locationData, END_POINT_DEPARTMENT } from "../const";
import { useNavigate, Link } from "react-router-dom";
import { showErrorToast, showSuccessToast } from "../utils/toast";
import CompanyCheckboxPicker from '../components/CompanyCheckboxPicker';
import SearchableSelect, {
  FormikSearchableSelect,
  buildSelectOptions,
  buildStringSelectOptions,
} from '../components/SearchableSelect';

export const GeneralInfoForm = () => {

    const navigate = useNavigate();
    const [stateList, setStateList] = useState([]);
    const [cityList, setCityList] = useState([]);
    const [industry, setIndustry] = useState([]);
    const [subIndustry, setSubIndustry] = useState([]);

    const [loadingSubIndustry, setLoadingSubIndustry] = useState(false);

    const [contactForms, setContactForms] = useState([
        { name: "", email: "", phone: "", designation: "", department: "" }
    ]);

    const [formErrors, setFormErrors] = useState([{ name: '', email: '', phone: '', designation: '', department: '' }]);

    const [department, setDepartment] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [allowMultipleCompanies, setAllowMultipleCompanies] = useState(false);

    const rawToken = sessionStorage.getItem('quotetoken');
    const token = rawToken?.replace(/^"|"$/g, '');

    const contactValidationSchema = Yup.object({
      name: Yup.string().required("Customer name is required"),
      email: Yup.string().email("Invalid email format").required("Email is required"),
      // phone: Yup.string()
      //   // .required("Mobile number is required")
      //   .matches(/^\d{10}$/, "Mobile number must be exactly 10 digits"),
      phone: Yup.string().matches(/^\d{10}$/, {
        message: "Mobile number must be exactly 10 digits",
        excludeEmptyString: true,
      }),
      designation: Yup.string().required("Designation is required"),
      department: Yup.string()
        .notOneOf(["0"], "Please select a valid department")
        .required("Please select a department"),
    });

  const formik = useFormik({
    initialValues: {
      name: "",
      mobile: "",
      address_line1: "",
      address_line2: "",
      country: "",
      state: "",
      city: "",
      pincode: "",
      gst: "",
      industry_segment: "",
      industry_sub_segment: "",
      company_ids: [],
    },
    validationSchema: Yup.object({
        name: Yup.string().required("Customer name is required"),
        mobile: Yup.string()
        // .required("Mobile number is required")
        .matches(/^\d{10}$/, "Number must be exactly 10 digits"),
        address_line1: Yup.string().required("Address is required"),
        country: Yup.string()
            .notOneOf(["0"], "Please select a valid country")
            .required("please select a country"),
        state: Yup.string()
            .notOneOf(["0"], "Please select a valid state")
            .required("please select a state"),
        city: Yup.string()
            .notOneOf(["0"], "Please select a valid city")
            .required("please select a city"),
        pincode: Yup.string().required("Pin code is required"),
        // gst: Yup.string().required("GST no. is required"),
        industry_segment: Yup.string()
            .notOneOf(["0"], "Please select a valid industry")
            .required("Industry Segment is required"),
        // industry_sub_segment: Yup.string().required("Industry Sub Segment is required")
        company_ids: allowMultipleCompanies ? Yup.array().min(1, "Select at least one company") : Yup.array(),
    }),

    onSubmit: async (values, { setSubmitting }) => {

        const updatedErrors = [];
        let contactFormValid = true;
        
        for (let i = 0; i < contactForms.length; i++) {
          try {
            await contactValidationSchema.validate(contactForms[i], { abortEarly: false });
            updatedErrors[i] = {};
          } catch (err) {
            contactFormValid = false;
            const fieldErrors = {};
            err.inner.forEach(e => {
              fieldErrors[e.path] = e.message;
            });
            updatedErrors[i] = fieldErrors;
          }
        }
        setFormErrors(updatedErrors);
        

  if (!contactFormValid) {
    showErrorToast('Please fix errors in customer contact form before submitting.')
    return;
  }


      try {

        const payload = {
            ...values,
            representatives: contactForms,  // <-- append contacts array here
        };

        setSubmitting(true);

        const response = await axios.post(API_URL + END_POINT_CUSTOMERS, payload,
            {
                headers: {
                    'Accept': 'application/json',
                    Authorization: `Bearer ${token}`,
                }
            }
        );
        showSuccessToast(response.data.message)
        navigate('/customers');
      } catch (error) {
        showErrorToast(error.response?.data?.message || 'Failed to create Customer.')
      }finally {
        setSubmitting(false);
      }
    }
  });

  useEffect(() => {
      const fetchDepartmentlist = async () => {
        try {
          const response = await axios.get(API_URL + END_POINT_DEPARTMENT, {
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${token}`,
            }
          });
  
          if (response.data.status && Array.isArray(response.data.data)) {
            setDepartment(response.data.data);
          }
        } catch (error) {
          console.error('Error fetching department:', error);
        }
      };
  
      fetchDepartmentlist();
    }, []);

    useEffect(() => {
      axios.get(`${API_URL}auth/context`, {
        headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
      }).then(({ data }) => {
        setCompanies(data.data?.companies || []);
        setAllowMultipleCompanies(data.data?.allow_multiple_companies || false);
      }).catch(() => {});
    }, [token]);
  

  useEffect(() => {
    const country = formik.values.country;
    if (country && locationData[country]) {
      setStateList(Object.keys(locationData[country].states));
      formik.setFieldValue("state", "");  // reset
      formik.setFieldValue("city", "");
      setCityList([]);
    }
  }, [formik.values.country]);
  
  useEffect(() => {
    const country = formik.values.country;
    const state = formik.values.state;
    if (country && state && locationData[country]?.states[state]) {
      setCityList(locationData[country].states[state]);
      formik.setFieldValue("city", "");
    }
  }, [formik.values.state]);
  

    useEffect(() => {
      const fetchIndustrylist = async () => {
        try {
          const response = await axios.get(API_URL + END_POINT_INDUSTRIES, {
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${token}`,
            }
          });
    
          if (response.data.status && Array.isArray(response.data.data)) {
            setIndustry(response.data.data);
          }
        } catch (error) {
          console.error('Error fetching industry:', error);
        } 
      };
    
      const timeout = setTimeout(() => {
        fetchIndustrylist();
      });
    
      return () => clearTimeout(timeout);
    }, [token]);

    const handleIndustryChange = async (selectedIndustryId) => {
        formik.setFieldValue("industry_segment", selectedIndustryId);
        
        if (selectedIndustryId && selectedIndustryId !== "0") {
            setLoadingSubIndustry(true);
          try {
            const response = await axios.get(`${API_URL}${END_POINT_SUB_INDUSTRIES}/${selectedIndustryId}`, {
              headers: {
                Accept: 'application/json',
                Authorization: `Bearer ${token}`,
              },
            });
      
            if (response.data.status && Array.isArray(response.data.data)) {
              setSubIndustry(response.data.data);
            } else {
              setSubIndustry([]);
            }
          } catch (error) {
            console.error("Error fetching sub-industries:", error);
            setSubIndustry([]);
          } finally {
            setLoadingSubIndustry(false); // stop loading
          }
        } else {
          setSubIndustry([]);
        }
    };

    const handleInputChange = async (index, field, value) => {
        const updatedForms = [...contactForms];
        updatedForms[index][field] = value;
        setContactForms(updatedForms);
      
        try {
          await contactValidationSchema.validateAt(field, updatedForms[index]);
          const updatedErrors = [...formErrors];
          if (updatedErrors[index]) {
            updatedErrors[index][field] = "";
            setFormErrors(updatedErrors);
          }
        } catch (err) {
          const updatedErrors = [...formErrors];
          if (!updatedErrors[index]) updatedErrors[index] = {};
          updatedErrors[index][field] = err.message;
          setFormErrors(updatedErrors);
        }
      };
      

    const addNewContact = () => {
      setContactForms([
        ...contactForms,
        { name: "", email: "", phone: "", designation: "", department: "" }
      ]);
      setFormErrors([...formErrors, {}]);
    };    

    const removeContact = (indexToRemove) => {
        setContactForms((prevForms) =>
          prevForms.filter((_, index) => index !== indexToRemove)
        );
    };

    const industryOptions = useMemo(() => buildSelectOptions(industry), [industry]);
    const subIndustryOptions = useMemo(() => buildSelectOptions(subIndustry), [subIndustry]);
    const countryOptions = useMemo(
      () => buildStringSelectOptions(Object.keys(locationData)),
      []
    );
    const stateOptions = useMemo(() => buildStringSelectOptions(stateList), [stateList]);
    const cityOptions = useMemo(() => buildStringSelectOptions(cityList), [cityList]);
    const departmentOptions = useMemo(() => buildSelectOptions(department), [department]);

    return (
      <>
        <Card border="light" className="bg-white shadow-sm mb-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="mb-0">Add Customer</h5>
              <Link to="/customers" className="text-decoration-none btn-quote-back d-inline-flex align-items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-arrow-left-circle me-1"><circle cx="12" cy="12" r="10"></circle><polyline points="12 8 8 12 12 16"></polyline><line x1="16" y1="12" x2="8" y2="12"></line></svg>
                Back to Listing
              </Link>
            </div>
    
            <Form noValidate onSubmit={formik.handleSubmit}>
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group controlId="name">
                    <Form.Label>
                      Customer Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      {...formik.getFieldProps("name")}
                      isInvalid={formik.touched.name && formik.errors.name}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.name}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group controlId="mobile">
                    {/* <Form.Label>Mobile no. <span className="text-danger">*</span></Form.Label> */}
                    <Form.Label>Mobile no.</Form.Label>
                    <Form.Control type="text" {...formik.getFieldProps("mobile")} isInvalid={formik.touched.mobile && formik.errors.mobile} />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.mobile}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                {(allowMultipleCompanies || companies.length > 1) && (
                  <Col md={12} className="mb-3">
                    <CompanyCheckboxPicker
                      companies={companies}
                      selectedIds={formik.values.company_ids}
                      onChange={(companyIds) => formik.setFieldValue('company_ids', companyIds)}
                      error={formik.touched.company_ids && formik.errors.company_ids}
                    />
                  </Col>
                )}
              </Row>
              <Row>
                <Col sm={6} className="mb-3">
                  <Form.Group controlId="industry_segment">
                    <Form.Label>Industry Segment <span className="text-danger">*</span></Form.Label>
                    <FormikSearchableSelect
                      formik={formik}
                      name="industry_segment"
                      options={industryOptions}
                      placeholder="Search industry..."
                      onChange={handleIndustryChange}
                    />
                  </Form.Group>
                </Col>
    
                <Col sm={6} className="mb-3">
                  <Form.Group controlId="industry_sub_segment">
                    <Form.Label>Industry Sub Segment</Form.Label>
                    {loadingSubIndustry ? (
                      <div className="d-flex align-items-center">
                        <div className="spinner-border text-primary me-2" role="status" style={{ width: '1.5rem', height: '1.5rem' }}>
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <span>Loading Sub Segments...</span>
                      </div>
                    ) : (
                      <FormikSearchableSelect
                        formik={formik}
                        name="industry_sub_segment"
                        options={subIndustryOptions}
                        placeholder="Search sub segment..."
                      />
                    )}
                  </Form.Group>
                </Col>
              </Row>
    
              <Row>
                <Col sm={6} className="mb-3">
                  <Form.Group controlId="address_line1">
                    <Form.Label>Address line 1 <span className="text-danger">*</span></Form.Label>
                    <Form.Control as="textarea" {...formik.getFieldProps("address_line1")} isInvalid={formik.touched.address_line1 && formik.errors.address_line1} />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.address_line1}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
    
                <Col sm={6} className="mb-3">
                  <Form.Group controlId="address_line2">
                    {/* <Form.Label>Address line 2 <span className="text-danger">*</span></Form.Label> */}
                    <Form.Label>Address line 2</Form.Label>
                    <Form.Control as="textarea" {...formik.getFieldProps("address_line2")} isInvalid={formik.touched.address_line2 && formik.errors.address_line2} />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.address_line2}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
    
              <Row>
                <Col sm={6} className="mb-3">
                  <Form.Group controlId="pincode">
                    <Form.Label>Pin Code <span className="text-danger">*</span></Form.Label>
                    <Form.Control type="text" {...formik.getFieldProps("pincode")} isInvalid={formik.touched.pincode && formik.errors.pincode} />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.pincode}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
    
                <Col md={6} className="mb-3">
                  <Form.Group controlId="gst">
                    {/* <Form.Label>GST NO. <span className="text-danger">*</span></Form.Label> */}
                    <Form.Label>GST NO.</Form.Label>
                    <Form.Control type="text" {...formik.getFieldProps("gst")} isInvalid={formik.touched.gst && formik.errors.gst} />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.gst}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
    
              <Row>
                <Col sm={4} className="mb-3">
                  <Form.Group controlId="country">
                    <Form.Label>Country <span className="text-danger">*</span></Form.Label>
                    <FormikSearchableSelect
                      formik={formik}
                      name="country"
                      options={countryOptions}
                      placeholder="Search country..."
                    />
                  </Form.Group>
                </Col>
    
                <Col sm={4} className="mb-3">
                  <Form.Group controlId="state">
                    <Form.Label>State <span className="text-danger">*</span></Form.Label>
                    <FormikSearchableSelect
                      formik={formik}
                      name="state"
                      options={stateOptions}
                      placeholder="Search state..."
                      isDisabled={!formik.values.country}
                    />
                  </Form.Group>
                </Col>
    
                <Col sm={4} className="mb-3">
                  <Form.Group controlId="city">
                    <Form.Label>City <span className="text-danger">*</span></Form.Label>
                    <FormikSearchableSelect
                      formik={formik}
                      name="city"
                      options={cityOptions}
                      placeholder="Search city..."
                      isDisabled={!formik.values.state}
                    />
                  </Form.Group>
                </Col>
              </Row>
    
              {contactForms.map((form, index) => (
                <Card key={index} border="light" className="bg-white shadow-sm mb-4">
                  {contactForms.length > 1 && (
                    <Button variant="danger" className="position-absolute top-0 end-0 m-3 p-1 rounded-pill" onClick={() => removeContact(index)}>×</Button>
                  )}
                  <Card.Body>
                    <h5 className="mb-4">Customer Representative {index + 1}</h5>
    
                    <Row>
                      <Col md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label>Customer Name <span className="text-danger">*</span></Form.Label>
                          <Form.Control type="text" value={form.name} onChange={(e) => handleInputChange(index, "name", e.target.value)} isInvalid={!!formErrors[index]?.name} />
                          <Form.Control.Feedback type="invalid">{formErrors[index]?.name}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
    
                      <Col md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                          <Form.Control type="email" value={form.email} onChange={(e) => handleInputChange(index, "email", e.target.value)} isInvalid={!!formErrors[index]?.email} />
                          <Form.Control.Feedback type="invalid">{formErrors[index]?.email}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
    
                    <Row>
                      <Col md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label>Mobile No.</Form.Label>
                          <Form.Control type="text" value={form.phone} onChange={(e) => handleInputChange(index, "phone", e.target.value)} isInvalid={!!formErrors[index]?.phone} />
                          <Form.Control.Feedback type="invalid">{formErrors[index]?.phone}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
    
                      <Col md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label>Department <span className="text-danger">*</span></Form.Label>
                          <SearchableSelect
                            options={departmentOptions}
                            value={form.department}
                            onChange={(value) => handleInputChange(index, "department", value)}
                            placeholder="Search department..."
                            isInvalid={!!formErrors[index]?.department}
                            errorMessage={formErrors[index]?.department}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
    
                    <Row>
                      <Col md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label>Designation <span className="text-danger">*</span></Form.Label>
                          <Form.Control type="text" value={form.designation} onChange={(e) => handleInputChange(index, "designation", e.target.value)} isInvalid={!!formErrors[index]?.designation} />
                          <Form.Control.Feedback type="invalid">{formErrors[index]?.designation}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
    
                    {index === contactForms.length - 1 && (
                      <div className="d-flex justify-content-between mt-3">
                        <Button variant="success" onClick={addNewContact}>Add new customer contact</Button>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              ))}
    
              <div className="mt-2">
                <Button 
                  variant="primary" 
                  type="submit"
                  disabled={formik.isSubmitting}
                >
                  Save
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </>
    );    
};